import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqButton, EiqSelect, EiqCheckbox } from 'eiq-storybook';
import moment from 'moment';
import { sortBy } from 'lodash';

import { useCurrenciesLazy } from 'src/hooks/currency';
import Attachments from 'src/components/Attachments';
import { EntityDeleteConfirm } from 'src/components/EntityDeleteConfirm';
import CapEditTextAreaField from 'src/components/CapFieldsNew/CapEditTextAreaField';
import CapEditInputField from 'src/components/CapFieldsNew/CapEditInputField';
import CapEditDatepickerField from 'src/components/CapFieldsNew/CapEditDatepickerField';
import { CapActionComments } from './CapActionComments/CapActionComments';
import { actionItemsEditable as checkEditableStatus, ACTION_STATUS_OPTIONS, ACTION_RESOURCE_TYPE_VALUES, ACTION_RESOURCE_INVESTMENT_DEFAULT_VALUES, ACTION_RESOURCE_NUMBER_OF_STAFF_DEFAULT_VALUES, getCapManagerActionStatusOptions } from './utils';
import s from './CapAction.styles';
import { ResourceNoOfStaffDays } from './CapActionResource/ResourceNoOfStaffDays';
import { ResourceInvestment } from './CapActionResource/ResourceInvestment';
 
export const CapAction = ({ action, index, actions, editableForExternalUser, setFindingsValue, finding, errors, originalAction, isCapManager, isAdmin, reviewMethod, currentUserId, overallCapStatus, capType }) => {
  const { t } = useTranslation();

  const [collapsed, setCollapsed] = useState(!!action.id);
  const [showConfirmDeletePopup, setShowConfirmDeletePopup] = useState(false);
  const actionStatusValue = ACTION_STATUS_OPTIONS.find(sts => sts.val === action?.status);
  const [selectedActionStatus, setSelectedActionStatus] = useState(action?.status && actionStatusValue ? actionStatusValue?.label : ACTION_STATUS_OPTIONS.find(sts => sts.val === 'review_pending').label);
  const editableItemStatus = checkEditableStatus(action?.status, action?.id, originalAction.status, isCapManager, isAdmin, editableForExternalUser, overallCapStatus, capType);
  const comments = ([...(originalAction.comments || [])]).sort((a, b) => b.id - a.id);

  const [deleteResource, setDeleteResource] = useState(false);
  const resourceInvestment = action?.resources?.find(resource => resource?.resourceType === ACTION_RESOURCE_TYPE_VALUES.investment);
  const resourceNoOfStaff = action?.resources?.find(resource => resource?.resourceType === ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays);
  const investment =  ['details', 'currencyId', 'costs', 'id'];
  const noOfStaff = [...investment, 'numberOfStaff', 'staffDayType', 'staffDayValue', 'averageDayRate'];
  const checkInvestmentValuePresence = investment.some((key) => resourceInvestment && resourceInvestment[key]); 
  const checkNoOfStaffValuePresence = noOfStaff.some((key) => resourceNoOfStaff && resourceNoOfStaff[key]);

  const [resourceInvestmentChecked, setResourceInvestmentChecked] = useState(!!checkInvestmentValuePresence);
  const [resourceNoOfStaffChecked, setResourceNoOfStaffChecked] = useState(!!checkNoOfStaffValuePresence);
  const [deleteActionOrResource, setDeleteActionOrResource] = useState({});

  const [loadCurrenciesLazy, { data }] = useCurrenciesLazy({
    limit: 1000,
    offset: 0,
  });

  useEffect(() => {
    loadCurrenciesLazy();
  }, []);

  useEffect(() =>{
    if(action){
      setSelectedActionStatus(action?.status && actionStatusValue ? actionStatusValue?.label : ACTION_STATUS_OPTIONS.find(sts => sts.val === 'review_pending').label);
    }
  }, [action])

  useEffect(() => {
    if (deleteResource) {
      if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays) setResourceNoOfStaffChecked(false);
      else if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.investment) setResourceInvestmentChecked(false);
    }
  }, [deleteResource]);

  const currencies = ((data && data.currencies) || []);
  const currencyOptions = useMemo(() => sortBy(currencies, ['code'])
    .map(currency => ({ value: currency.id, label: `${currency.code}` })),
    [currencies]);

  const setValue = (fieldName, val) => {
    const updatedFinding = {...finding};
    const updatedActions = actions ? [...actions] : [];

    updatedActions[index][fieldName] = val;
    updatedActions[index].modified = true;
    updatedFinding.actions = updatedActions;
    
    setFindingsValue(updatedFinding);
  };

  const attachmentComponent = () => {
    return (
      <Attachments
        doRerender={true}
        canNotDownload={!(isCapManager || isAdmin) ? !(editableForExternalUser) : false}
        attachmentIds={action.attachmentsMeta || []}
        attachmentsLength={action.attachmentIds.length}
        disabled={!editableItemStatus?.attachments}
        entity="attachments"
        onChange={async (_filename, _entity, id, meta) => {
          setValue('attachmentIds', [...action.attachmentIds, id]);
          setValue('attachmentsMeta', [...action.attachmentsMeta, meta]);
        }}
        onDeleteFile={(name, attId) => {
          if (name !== 'loading.file' && attId) {
            const attachmentIds = [...action.attachmentIds].filter(att => att !== attId);
            const attachmentsMeta = [...action.attachmentsMeta].filter(att => att.attId !== attId);
            setValue('attachmentIds', attachmentIds);
            setValue('attachmentsMeta', attachmentsMeta);
          }
        }}
        style="cap"
      />
    );
  };

  const removeActionFromCollection = ({ id, index: indx }) => {
    const updatedActions = actions ? [...actions] : [];
    const updatedFinding = finding;

    if (id) {
      const filteredActions = updatedActions.filter((actionItem) => actionItem.id !== id);
      updatedFinding.actions = filteredActions;
      setFindingsValue(updatedFinding);
    } else if (indx || indx === 0) {
      const filteredActions = updatedActions.filter((actionItem, index2) => indx !== index2);
      updatedFinding.actions = filteredActions;
      setFindingsValue(updatedFinding);
    }
  };


  const removeResourceFromList = ({ id, index: indx }) => {
    const updatedActions = actions ? [...actions] : [];
    const updatedFinding = finding;
    let resouresArr = [];
    if (id) {
      const filteredActions = {...updatedActions[index]};
      const filteredResources = filteredActions.resources?.findIndex((resource) => resource.id === id);
      updatedActions[index].resources[filteredResources] = [];
      resouresArr = updatedActions[index].resources ? [...updatedActions[index].resources] : [];
      resouresArr.splice(filteredResources, 1);
      updatedFinding.actions = updatedActions;
      setValue('resources', resouresArr);
      setFindingsValue(updatedFinding);
    } else if (indx || indx === 0) {
      let obj = {};
      const resourceIndex = updatedActions[index].resources?.findIndex((resource) => deleteActionOrResource?.resourceName === resource.resourceType);
      if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.investment) {
        obj = {...ACTION_RESOURCE_INVESTMENT_DEFAULT_VALUES};
      }else if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays) {
        obj = {...ACTION_RESOURCE_NUMBER_OF_STAFF_DEFAULT_VALUES};
      }
      if(resourceIndex !== undefined && resourceIndex !== -1) updatedActions[index].resources[resourceIndex] = obj;
      resouresArr = updatedActions[index].resources ? [...updatedActions[index].resources] : [];
      resouresArr.splice(resourceIndex, 1);
      
      updatedFinding.actions = updatedActions;
      setValue('resources', resouresArr);
      setFindingsValue(updatedFinding);
    }

  };

  useEffect(() => {
    if (deleteActionOrResource?.item === 'action' || (deleteActionOrResource?.item === 'resource' && deleteActionOrResource?.id)){
       setShowConfirmDeletePopup(true);
    }else if(deleteActionOrResource?.item === 'resource' && !deleteActionOrResource?.id){
      removeResourceFromList({ index: 0 });      
    }
  }, [deleteActionOrResource]);

  const deleteAction = async () => {
    if (deleteActionOrResource?.item === 'action') {
      const { id } = action;
      if (id) {
        // await deleteCapAction(id);
        removeActionFromCollection({ id });
      } else {
        removeActionFromCollection({ index });
      }
    } else if (deleteActionOrResource?.item === 'resource') {
      setDeleteResource(true);const { id } = deleteActionOrResource;
      if (deleteActionOrResource?.id) {
        // await deleteActionResource(id);
        removeResourceFromList({ id, index: 0 });
      } else {
        removeResourceFromList({ index: 0 });
      }
    }
  };

  const handleAddComment = (comment, commentId = 0) => {
    const updatedComments = [...(action.comments || [])];
    const existingCommentIndex = updatedComments.findIndex(v => v.id === commentId);  
    if (existingCommentIndex   !== -1) {
      updatedComments[existingCommentIndex] = { id: commentId, comment};
    } else {        
      const newCommentIndex = updatedComments.findIndex(v => v.id === null);
      if (newCommentIndex   !== -1) {
        updatedComments[newCommentIndex] = { id: null, comment};
      }else
        updatedComments.push({ id: null, comment});
    }
    setValue('comments', updatedComments);
  };

  const removeComment = (indx) => {
    const updatedComments = [...(action.comments || [])];
    updatedComments.splice(indx, 1);
    setValue('comments', updatedComments);
  };

  const setResourcesDefaultValue = (selectedResource, resourceType, defaultValues) => {
    const currentResources = action?.resources;
    const resourceCurrentIndex = currentResources?.findIndex((item) => selectedResource && (item?.resourceType === resourceType));
    const resourceDefaultObj = {...defaultValues};
    resourceDefaultObj.resourceType = resourceType;
    const updatedDefaultResources = currentResources ? [...currentResources] : [];
    if(resourceCurrentIndex === 0 || resourceCurrentIndex !== -1 ){
      updatedDefaultResources[resourceCurrentIndex] = resourceDefaultObj;
    }else{      
      const resourceIndex = currentResources?.findIndex((item) => resourceType === item?.resourceType);
      if(resourceIndex === 0 || resourceIndex !== -1 ) updatedDefaultResources[resourceIndex] = resourceDefaultObj;
      else updatedDefaultResources[currentResources ? currentResources.length : 0] = resourceDefaultObj;
    }       
    setValue('resources', updatedDefaultResources)
  };
  
  return (
    <>
      <s.CapActionContainer >
        <s.CapActionHeader >
          <s.Title className="pl-15">{`${t('ACTION')} ${index + 1}`}</s.Title>
          <s.CollapsableWrapper className="pr-15 collapsable" type="button" onClick={() => setCollapsed(!collapsed)} data-testid="collapsable-button" >
            {collapsed ?
              <s.CollapseIcon name="collapseIcon" className="cursor-pointer" /> :
              <s.ExpandIcon name="expandIcon" className="cursor-pointer" />
            }
          </s.CollapsableWrapper>
        </s.CapActionHeader>

        <s.CapActionDetailsWrapper>
          <s.CapBriefSection>
            <ul>
              <li>
                <s.TitleSmall>{t('DUE_DATE')}</s.TitleSmall>
                <div>
                  <CapEditDatepickerField 
                    placeholderText={t('CHOOSE')}
                    wrapperClassName="eiqWrapper"
                    selectedDate={action.dueDate}
                    disabled={!editableItemStatus?.actionReference}
                    onChange={(val => setValue('dueDate', val))}
                    error={errors?.dueDate}
                    required
                  />
                </div>
              </li>
              <li>
                <s.TitleSmall>{t('COMPLETION_DATE')}</s.TitleSmall>
                <div>
                  <CapEditDatepickerField 
                    placeholderText={t('CHOOSE')}
                    wrapperClassName="eiqWrapper"
                    selectedDate={action.actualCompletionOfActionDate}
                    disabled={!editableItemStatus?.actionReference}
                    onChange={(val => setValue('actualCompletionOfActionDate', val))}
                    error={errors?.actualCompletionOfActionDate}
                    required
                  />
                </div>
              </li>
              <li>
                <s.TitleSmall>{t('CAP_MANAGER_DASHBOARD_RESPONSIBLE_PERSON')}</s.TitleSmall>
                <div>
                  <CapEditInputField
                    wrapperClassName='InputWrapper eiqWrapper'
                    value={action.responsiblePerson}
                    isEditable={editableItemStatus?.actionReference}
                    onBlur={(val) => setValue("responsiblePerson", val)}
                    hasLabel={true}
                    errorMessage={errors?.responsiblePerson}
                    label={'CLICK_HERE_TO_ENTER_TEXT'}
                  />
                </div>
              </li>
              <li>
                <s.TitleSmall className='not-mandatory'>{t('LATEST_STATUS_UPDATE_DATE')}</s.TitleSmall>
                <div>{originalAction?.statusDate ? moment(new Date(originalAction?.statusDate)).format('DD-MM-YYYY') : '-'}</div> 
              </li>
            </ul>
            <s.ActionStatusWrapper>
              <EiqSelect
                name="status"
                error={errors?.status}
                touched={true}
                backspaceRemovesValue={false}
                isClearable={false}
                wrapperClass='actionStatusWrapper'
                hasCustomChips
                showLabel={false}
                isDisabled={!editableItemStatus?.actionStatus}
                onChange={(_name, option) => {
                  setSelectedActionStatus(option.label);
                  setValue("status", option.val);
                }}
                options={isCapManager ? getCapManagerActionStatusOptions(reviewMethod, overallCapStatus) : ACTION_STATUS_OPTIONS}
                placeholder=''
                value={ACTION_STATUS_OPTIONS.find(actionStatus => actionStatus.label === selectedActionStatus)}
              />
            </s.ActionStatusWrapper>
          </s.CapBriefSection>

          <s.CapDetailSection>
            <s.LeftSection>
              <s.DetailWrapper>
                <s.Title className='desc-title'>{t('COLUMN_DESCRIPTION')}</s.Title>
                <div className='pr-20'>
                  <CapEditTextAreaField
                    isEditable={editableItemStatus?.actionReference}
                    name="description"
                    wrapperClassName='DescriptionInputWrapper eiqWrapper'
                    value={action.description}
                    rows={4}
                    onBlur={(val) => setValue('description', val)}
                    errorMessage={errors?.description}
                  />
                </div>
              </s.DetailWrapper>
              <s.ToggleWrapper>
                <div className={`collapsable pl-13 ${!collapsed ? 'expanded' : ''}`} data-testid="collapsable">
                  <s.DetailWrapper>
                    <s.Title>{t('ATTACHMENTS')}</s.Title>
                    {attachmentComponent()}
                  </s.DetailWrapper>
                  <s.DetailWrapper className='pl-0'>
                    <CapActionComments
                      canAddComment={editableItemStatus?.comments}
                      isEditable={editableItemStatus?.comments}
                      originalComments={comments || []}
                      comments={action.comments||[]}
                      handleAddComment={handleAddComment}
                      removeComment={removeComment}
                      index={index}
                      id={action.id}
                      isCapManager={isCapManager}
                      currentUserId={currentUserId}
                    />
                  </s.DetailWrapper>
                  { editableForExternalUser && !editableItemStatus?.hideDelete && (<s.DeleteActionWrapper>
                    <EiqButton variant='btn--outline-link' 
                               label={`X ${t('CAP_DELETE_ACTION')}`} 
                               variantColor='extreme'
                               variantTextColor='extreme'
                               disabled={!editableItemStatus?.actionReference}
                               onClick={() => {
                                setShowConfirmDeletePopup(true);
                                setDeleteActionOrResource({item: 'action'})
                              }}
                    />
                    </s.DeleteActionWrapper>
                  )}
                </div>
              </s.ToggleWrapper>
            </s.LeftSection>

            <s.RightSection>
              <s.ToggleWrapper overflow>
                <div className={`collapsable ${collapsed ? 'expanded' : ''}`}>   
                  {/* eslint-disable-next-line no-nested-ternary */
                    collapsed && ((resourceInvestment?.id || !!resourceInvestmentChecked) ?
                      <>
                        <s.Title>{t('RESOURCES_INVESTMENT')}</s.Title>
                        <s.ResourceDetailWrapper className='flex'>
                          <s.ResourceTitleLabel>{t('TOTAL_COST')}</s.ResourceTitleLabel>
                          <s.ResourceLabel>{resourceInvestment?.costs ?? '-'} {currencies.find(currency => currency.id === resourceInvestment?.currencyId)?.code}</s.ResourceLabel>
                        </s.ResourceDetailWrapper>
                      </>
                      : !resourceInvestment?.id && !resourceNoOfStaff?.id ? <s.Title>{t('RESOURCES')}</s.Title> : <></>)
                  }
                  {collapsed && (resourceNoOfStaff?.id || !!resourceNoOfStaffChecked) &&
                    <>
                      <s.Title>{t('RESOURCES_STAFF_DAYS')}</s.Title>
                      <s.ResourceDetailWrapper className='flex'>
                        <s.ResourceTitleLabel>{t('TOTAL_COST')}</s.ResourceTitleLabel>
                        <s.ResourceLabel>{resourceNoOfStaff?.costs ?? '-' } {currencies.find(currency => currency.id === resourceNoOfStaff?.currencyId)?.code}</s.ResourceLabel>
                      </s.ResourceDetailWrapper>
                    </>}
                </div>
                <div className={` ${!collapsed ? 'expanded' : 'collapsable'}`}>
                  <s.ResourceWrapper className={` ${!collapsed ? 'expanded' : 'collapsable'}`}>
     {!collapsed && <EiqCheckbox sizeClass='checkbox--md' ariaLabel="Checkbox" label={t('RESOURCES_INVESTMENT')} value={resourceInvestment?.id} checked={resourceInvestmentChecked}
                                 onChange={(e) => {
                                  setDeleteResource(false);
                                  const checkboxStatus = e.target.checked;
                                  setResourceInvestmentChecked(checkboxStatus);
                                  if (!checkboxStatus) {
                                    setDeleteActionOrResource({ item: 'resource', id: resourceInvestment?.id, resourceName:  ACTION_RESOURCE_TYPE_VALUES.investment });
                                  }else{
                                    setResourcesDefaultValue(resourceInvestment, ACTION_RESOURCE_TYPE_VALUES.investment, ACTION_RESOURCE_INVESTMENT_DEFAULT_VALUES)
                                  }
                                }}
                                 disabled={!editableItemStatus?.resources} />}
                    {!collapsed && resourceInvestmentChecked && <ResourceInvestment errors={errors?.resources} actionId={action.id} resources={action?.resources} setValue={setValue} currencyOptions={currencyOptions} resource={resourceInvestment} isAdmin={isAdmin} editableStatus={editableItemStatus?.resources} isCapManager={isCapManager}></ResourceInvestment>}

                  </s.ResourceWrapper>
                  <s.ResourceWrapper className={` ${!collapsed ? 'expanded' : 'collapsable'}`}>
           {!collapsed && <EiqCheckbox sizeClass='checkbox--md' ariaLabel="Checkbox" label={t('RESOURCES_STAFF_DAYS')} value={resourceNoOfStaff?.id} checked={resourceNoOfStaffChecked}
                                       onChange={(e) => {
                                        setDeleteResource(false);
                                        const checkboxStatus = e.target.checked;
                                        setResourceNoOfStaffChecked(checkboxStatus);
                                        if (!checkboxStatus) {
                                          setDeleteActionOrResource({ item: 'resource', id: resourceNoOfStaff?.id, resourceName: ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays });
                                        }else{
                                          setResourcesDefaultValue(resourceNoOfStaff, ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays, ACTION_RESOURCE_NUMBER_OF_STAFF_DEFAULT_VALUES)
                                        }
                                      }}
                                       disabled={!editableItemStatus?.resources} />}
                          {!collapsed && resourceNoOfStaffChecked && <ResourceNoOfStaffDays errors={errors?.resources} actionId={action.id} resources={action?.resources} setValue={setValue} currencyOptions={currencyOptions} resource={resourceNoOfStaff} isAdmin={isAdmin} editableStatus={editableItemStatus?.resources} isCapManager={isCapManager}></ResourceNoOfStaffDays>}

                  </s.ResourceWrapper>
                </div>
                {((!resourceNoOfStaffChecked && !resourceInvestmentChecked) || typeof errors?.resources === 'string') && <s.ErrorMessageWrapper >{t('ONE_RESOURCE_REQUIRED')}</s.ErrorMessageWrapper>}
              </s.ToggleWrapper>
            </s.RightSection>
          </s.CapDetailSection>
        </s.CapActionDetailsWrapper>
      </s.CapActionContainer>
      {showConfirmDeletePopup && (
        <EntityDeleteConfirm
          id={'capdelete'}
          onClose={() => {
            if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.numberOfStaffDays)   setResourceNoOfStaffChecked(true);
            else if (deleteActionOrResource?.resourceName === ACTION_RESOURCE_TYPE_VALUES.investment) setResourceInvestmentChecked(true);
            setShowConfirmDeletePopup(false);
          }}
          onSubmit={() => deleteAction()}
          resourceName={'CapAction'}
        />
      )}
    </>
  );
};
